import { useState } from 'react';
import Axios from 'axios';
import { useAppSettings } from '../hooks/useAppSettings';
import { CategoryDocument } from './useCategoryDocuments';

interface UpsertCategoryDocumentParams {
    id?: number;
    filePath: string;
    category: string;
    orderNr?: number;
}

export const useUpsertCategoryDocument = () => {
    const { settings: appSettings } = useAppSettings();
    const [isUpserting, setIsUpserting] = useState(false);
    const [upsertError, setUpsertError] = useState<string | null>(null);
    const [upsertSuccess, setUpsertSuccess] = useState(false);

    const upsertCategoryDocument = async ({
        id = 0,
        filePath,
        category,
        orderNr = 0,
    }: UpsertCategoryDocumentParams): Promise<CategoryDocument | null> => {
        if (!appSettings?.api) {
            throw new Error('API settings not available');
        }

        setIsUpserting(true);
        setUpsertError(null);
        setUpsertSuccess(false);

        try {
            const payload = {
                Id: id,
                FilePath: filePath,
                Category: category,
                OrderNr: orderNr,
                LastUpdatedUtc: new Date().toISOString(),
            };

            // Use Axios so the MSAL interceptor acquires a fresh token automatically
            const response = await Axios.post(`${appSettings.api}UpsertCategoryDocument`, payload);

            const result: CategoryDocument = response.data;
            setUpsertSuccess(true);
            return result;
        } catch (error: any) {
            const message = error.response?.data?.message || error.message || 'Failed to save category document';
            setUpsertError(message);
            throw new Error(`Upsert failed (${error.response?.status ?? 'unknown'}): ${message}`);
        } finally {
            setIsUpserting(false);
        }
    };

    const clearMessages = () => {
        setUpsertError(null);
        setUpsertSuccess(false);
    };

    return {
        upsertCategoryDocument,
        isUpserting,
        upsertError,
        upsertSuccess,
        clearMessages,
    };
};
